import React, { useCallback, useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { faHome } from '@fortawesome/pro-duotone-svg-icons/faHome'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Trans } from 'react-i18next'
import { StyledButton } from '@/shared/ui/buttons'

interface RuntimeRecoveryFallbackProps {
    runtimeRecoveryType?: string | null
    kedo?: {
        env: () => {
            clearInfo: () => void
        }
    }
}

const noDataUrl = '/static/images/no-data-bro.svg'
const redirectDelaySeconds = 10
const recoveryPath = '/environment'

const RuntimeRecoveryFallback: React.FC<RuntimeRecoveryFallbackProps> = ({
    runtimeRecoveryType,
    kedo,
}) => {
    const [redirectIn, setRedirectIn] = useState(redirectDelaySeconds)

    const isAuthRecovery = runtimeRecoveryType === 'auth-session-recovery'

    // Go to /environment and drop env from LS + memory. Leaving a bad snapshot
    // lets checkLocalStorage restore it and can crash-loop on the next render.
    const goToRecoveryPath = useCallback(() => {
        try {
            window.localStorage.removeItem('environment')
        } catch {
            // Navigation still provides a clean recovery when storage is blocked.
        }

        try {
            kedo?.env()?.clearInfo()
        } catch {
            // Cleanup must not block redirect.
        }

        window.location.replace(recoveryPath)
    }, [kedo])

    useEffect(() => {
        setRedirectIn(redirectDelaySeconds)

        const countdownTimer = window.setInterval(() => {
            setRedirectIn((prev) => (prev > 0 ? prev - 1 : 0))
        }, 1000)

        const redirectTimer = window.setTimeout(() => {
            goToRecoveryPath()
        }, 1000 * redirectDelaySeconds)

        return () => {
            window.clearInterval(countdownTimer)
            window.clearTimeout(redirectTimer)
        }
    }, [goToRecoveryPath])

    return (
        <Box
            data-cy="runtime-recovery-fallback"
            sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                flexDirection: 'column',
                position: 'fixed',
                inset: 0,
                width: '100%',
                margin: 0,
                padding: '16px',
                backgroundColor: 'background.default',
                zIndex: 2147483647,
                textAlign: 'center',
            }}
        >
            <Box
                component="img"
                src={noDataUrl}
                alt="No data illustration"
                sx={{
                    width: 'min(35vw, 640px)',
                    maxWidth: '100%',
                    height: 'auto',
                }}
            />
            <Box
                href="https://storyset.com/data"
                component="a"
                title="no data icons"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                    mt: 0.5,
                    color: 'text.disabled',
                    fontSize: 10,
                    textDecoration: 'none',
                }}
            >
                Data illustrations by Storyset
            </Box>
            <Box sx={{ maxWidth: 680 }}>
                <h1>
                    <Trans i18nKey="Oops, an error occurred!">
                        Oops, an error occurred!
                    </Trans>
                </h1>
                <p>
                    {isAuthRecovery ? (
                        <Trans i18nKey="runtime_recovery_message_auth">
                            Your session has expired or the selected environment
                            is no longer available.
                        </Trans>
                    ) : (
                        <Trans i18nKey="runtime_recovery_message">
                            Please go back to the homepage and try again.
                        </Trans>
                    )}
                </p>
                <p>
                    <Trans i18nKey="runtime_recovery_redirect">
                        Redirecting in {{ seconds: redirectIn }} seconds...
                    </Trans>
                </p>
                <StyledButton
                    data-cy="runtime-recovery-action"
                    onClick={goToRecoveryPath}
                    color={'secondary'}
                    sx={{ mt: 1 }}
                >
                    <FontAwesomeIcon icon={faHome} />
                    &nbsp; <Trans i18nKey="Continue">Continue</Trans>
                </StyledButton>
            </Box>
        </Box>
    )
}

export default RuntimeRecoveryFallback
